import { List } from "@mui/material";
import { rankingApi } from "@bw/shared/api";
import { playerHandleSuffix } from "@bw/shared/components";
import { useFacilityNames } from "@bw/shared/hooks";
import type { RankedMatchResponse } from "@bw/shared/types/generated";
import { useQueries } from "@tanstack/react-query";
import { queryKeys } from "../../../common/query/queryClient";
import { MatchRow } from "./MatchRow";
import { sidesFor } from "./matchSides";

const NEUTRAL_NAME = "Player";
const NEUTRAL_FACILITY = "a facility";

/**
 * Ranked matches from one player's perspective. Opponent names come from each
 * player's public rating record (name + #id, or a neutral "Player"); detail
 * links open only for the viewer's own matches.
 */
export function MatchList({
  matches,
  viewerId,
  perspectiveId = viewerId,
}: {
  matches: readonly RankedMatchResponse[];
  /** The signed-in player: detail links open only for their own matches. */
  viewerId: string | null;
  /** Whose results these are (won/lost); defaults to the viewer. */
  perspectiveId?: string | null;
}) {
  const uids = Array.from(new Set(matches.flatMap((match) => match.players)));
  const ratingQueries = useQueries({
    queries: uids.map((uid) => ({
      queryKey: queryKeys.compete.rating(uid),
      queryFn: () => rankingApi.getRating(uid),
      retry: false,
    })),
  });
  function labelOf(uid: string): string {
    const index = uids.indexOf(uid);
    const name = index >= 0 ? ratingQueries[index]?.data?.display_name : null;
    return name?.trim() ? `${name} ${playerHandleSuffix(uid)}` : NEUTRAL_NAME;
  }
  const facilityNames = useFacilityNames(
    Array.from(new Set(matches.map((match) => match.facility_id))),
  );
  return (
    <List disablePadding>
      {matches.map((match) => {
        const sides = sidesFor(match.players, match.mode);
        const mySide: 1 | 2 | null =
          perspectiveId && sides.side1.includes(perspectiveId)
            ? 1
            : perspectiveId && sides.side2.includes(perspectiveId)
              ? 2
              : null;
        const opponentUids = mySide === 2 ? sides.side1 : sides.side2;
        return (
          <MatchRow
            key={match.match_id}
            match={match}
            perspectiveUserId={perspectiveId}
            canOpenDetail={Boolean(viewerId && match.players.includes(viewerId))}
            opponentName={opponentUids.length ? opponentUids.map(labelOf).join(" & ") : NEUTRAL_NAME}
            facilityName={facilityNames.get(match.facility_id) ?? NEUTRAL_FACILITY}
          />
        );
      })}
    </List>
  );
}
